import { flow } from 'fp-ts/function';
import { has } from 'lodash/fp';
import { compactArray } from '@mews/fp';
import { DateTimeService } from '@mews/framework';
import { TextKey, SearchType, RouteName } from '@mews-commander/enums';
import { ProductServiceOrder } from '@mews-commander/types';
import { Customer, Service } from '@mews-commander/data-access';
import { SpacedMiddleDot } from '@mews-commander/ui';
import { SearchResult, ResourceState, ServiceOrderState } from '@mews-commander/legacy-data-structures';
import { getContainer, getResolveRoute, store as getStore } from '@mews-commander/legacy-app-instance';
import { getFullName, getInlineAddress } from '@mews-commander/utils';
import { getResourceNameWithCategoryDetail } from '@mews-commander/legacy-entities/src/selectors/resources';

const stringifyOptionalProps = flow(compactArray, (arr) => arr.join(SpacedMiddleDot));

const RESERVATION_STATE_TEXT_KEY = {
    [ServiceOrderState.Started.value]: TextKey.CheckedIn,
    [ServiceOrderState.Processed.value]: TextKey.CheckedOut,
};

const createBillSearchResult = (r: $TSFixMe) => {
    const resolveRoute = getResolveRoute();

    return SearchResult({
        title: r.name,
        description: r.number,
        url: resolveRoute(RouteName.BillDetail, { id: r.id }),
        id: r.id,
        searchType: SearchType.Bill,
    });
};

const createCustomerSearchResult = (r: Customer) => {
    const resolveRoute = getResolveRoute();

    return SearchResult({
        title: getFullName(r),
        description: r.email || '',
        url: resolveRoute(RouteName.CustomerDetail, { id: r.id }),
        id: r.id,
        searchType: SearchType.Customer,
        options: r,
    });
};

const createEnterpriseProfileSearchResult = (r: $TSFixMe) => {
    const resolveRoute = getResolveRoute();

    return SearchResult({
        title: getFullName(r) || r.email || '',
        description: r.email || '',
        id: r.id,
        searchType: SearchType.EnterpriseProfile,
        options: r,
        url: resolveRoute(RouteName.EnterpriseProfileDetail, { enterpriseProfileId: r.id }),
    });
};

const createCompanySearchResult = (
    { name, department, iata, id, address, options, value }: $TSFixMe,
    additionalOptions: $TSFixMe
) => {
    const resolveRoute = getResolveRoute();
    const inlineAddress =
        address &&
        getInlineAddress({
            addressLine1: address.addressLine1,
            city: address.city,
            countryCode: address.countryCode,
            postalCode: address.postalCode,
        });

    return SearchResult({
        title: stringifyOptionalProps([name, department]) || value,
        description: stringifyOptionalProps([iata, inlineAddress]),
        url: resolveRoute(RouteName.CompanyDetail, { id }),
        id,
        searchType: SearchType.Company,
        options: { ...options, additionalOptions },
    });
};

const createReservationSearchResult = (r: $TSFixMe, { timezone }: $TSFixMe) => {
    const container = getContainer();
    const { appDateComponent, appLocalizationComponent } = container();
    const { getState } = getStore();
    const resolveRoute = getResolveRoute();
    const today = appDateComponent.today();

    const startDate = DateTimeService.fromUtcString(r.startUtc, timezone);
    const endDate = DateTimeService.fromUtcString(r.endUtc, timezone);
    const startDateString = appLocalizationComponent.localizeDate(startDate);
    const endDateString = appLocalizationComponent.localizeDate(endDate);

    const options = {};
    if (DateTimeService.isEqual(startDate, today) || DateTimeService.isEqual(endDate, today)) {
        (options as $TSFixMe).today = true;
    }

    const reservationState = has(r.state, RESERVATION_STATE_TEXT_KEY)
        ? appLocalizationComponent.localizeText(RESERVATION_STATE_TEXT_KEY[r.state])
        : // @ts-expect-error unhandled undefined
          appLocalizationComponent.localizeEnumify(ServiceOrderState.getByValue(r.state));

    const resourceName = r.assignedResourceId
        ? getResourceNameWithCategoryDetail(getState(), { resourceId: r.assignedResourceId, serviceId: r.serviceId })
        : null;

    const description = stringifyOptionalProps([
        `${startDateString} - ${endDateString}`,
        resourceName,
        reservationState,
    ]);

    return SearchResult({
        options,
        title: getFullName(r.customer),
        description,
        url: resolveRoute(RouteName.ReservationDetailStatus, { reservationId: r.id }),
        id: r.id,
        searchType: SearchType.Reservation,
    });
};

const createProductServiceOrderResult = (r: ProductServiceOrder & { service: Service }) => {
    const container = getContainer();
    const { appLocalizationComponent } = container();
    const resolveRoute = getResolveRoute();

    const serviceOrderName: string = appLocalizationComponent.localizeProperty(r.service.names);

    return SearchResult({
        searchType: SearchType.ProductServiceOrder,
        title: `${serviceOrderName} ${r.number}`,
        url: resolveRoute(RouteName.ProductServiceOrderDetail, { id: r.id }),
        id: r.id,
    });
};

const createReservationGroupSearchResult = (r: $TSFixMe) => {
    const resolveRoute = getResolveRoute();

    return SearchResult({
        searchType: SearchType.ReservationGroup,
        title: r.name,
        url: resolveRoute(RouteName.ReservationGroupDetail, { id: r.id }),
        id: r.id,
    });
};

const createResourceSearchResult = (r: $TSFixMe) => {
    const container = getContainer();
    const resolveRoute = getResolveRoute();

    return SearchResult({
        title: r.name,
        description: container().appLocalizationComponent.localizeEnumify(ResourceState.getByName(r.state)),
        url: resolveRoute(RouteName.ResourceState, { id: r.id }),
        id: r.id,
        searchType: SearchType.Resource,
    });
};

const createTravelAgencySearchResult = ({ id, name, department, iata, address, contractId }: $TSFixMe) => {
    const resolveRoute = getResolveRoute();

    const inlineAddress =
        address &&
        getInlineAddress({
            addressLine1: address.addressLine1,
            city: address.city,
            countryCode: address.countryCode,
            postalCode: address.postalCode,
        });

    return SearchResult({
        title: stringifyOptionalProps([name, department]),
        description: stringifyOptionalProps([iata, inlineAddress]),
        url: resolveRoute(RouteName.TravelAgencyContractDetail, { id: contractId }),
        id,
        searchType: SearchType.TravelAgency,
    });
};

const createCitySearchResult = (r: $TSFixMe) =>
    SearchResult({
        title: r.localName,
        searchType: SearchType.City,
        id: r.id,
    });

const createResourceBlockSearchResult = (r: $TSFixMe, { timezone }: $TSFixMe) => {
    const container = getContainer();
    const { appLocalizationComponent } = container();
    const resolveRoute = getResolveRoute();

    const startDate = DateTimeService.fromUtcString(r.startUtc, timezone);
    const endDate = DateTimeService.fromUtcString(r.endUtc, timezone);
    const startDateString = appLocalizationComponent.localizeDate(startDate);
    const endDateString = appLocalizationComponent.localizeDate(endDate);

    return SearchResult({
        title: r.name,
        url: resolveRoute(RouteName.ResourceBlockDetail, { id: r.id }),
        description: `${startDateString} - ${endDateString}`,
        id: r.id,
        searchType: SearchType.ResourceBlock,
    });
};

const createLocationsSearchResult = (r: $TSFixMe) =>
    SearchResult({
        title: r.freeformAddress,
        searchType: SearchType.Locations,
        id: r.id,
        options: r,
    });

const createNavigationSearchResult = (r: $TSFixMe) =>
    SearchResult({
        title: r.title,
        description: r.description,
        searchType: SearchType.Navigation,
        id: r.id,
        url: r.url,
    });

const searchResultCreatorsMap = {
    [SearchType.Bill]: createBillSearchResult,
    [SearchType.Customer]: createCustomerSearchResult,
    [SearchType.EnterpriseProfile]: createEnterpriseProfileSearchResult,
    [SearchType.Company]: createCompanySearchResult,
    [SearchType.Reservation]: createReservationSearchResult,
    [SearchType.ReservationGroup]: createReservationGroupSearchResult,
    [SearchType.Resource]: createResourceSearchResult,
    [SearchType.TravelAgency]: createTravelAgencySearchResult,
    [SearchType.City]: createCitySearchResult,
    [SearchType.ResourceBlock]: createResourceBlockSearchResult,
    [SearchType.Locations]: createLocationsSearchResult,
    [SearchType.Navigation]: createNavigationSearchResult,
    [SearchType.ProductServiceOrder]: createProductServiceOrderResult,
};

export default searchResultCreatorsMap;
